<script lang="ts">
  import { getContext } from 'svelte';
  import DynamicChildrenComponent from '$lib/components/DynamicChildrenComponent/DynamicChildrenComponent.svelte';
  import { DesignSystem as DesignSystemEnum } from '$lib/helpers/design-system';

  import type { HardwareModelViewerProps } from './types';

  export let entryId: HardwareModelViewerProps['entryId'];
  export let customCssClass: HardwareModelViewerProps['customCssClass'];
  export let locale: HardwareModelViewerProps['locale'];
  export let content: HardwareModelViewerProps['content'];
  export let device: HardwareModelViewerProps['device'];

  const isPWDSEnabled = getContext<DesignSystemEnum>('designSystem') === DesignSystemEnum.PublicWebDesignSystem;

  const defaultText = 'View in your space';
  const localeText: Partial<Record<SupportedLocale, string>> = {
    'es-US': 'Ver en tu espacio',
    'fr-CA': 'Afficher dans votre espace',
    'fr-FR': 'Afficher dans votre espace',
    'es-ES': 'Mostrar en tu espacio',
    'ca-ES': 'Mostra al teu espai',
    'ja-JP': '自分のスペースに表示する',
  };

  const text = localeText[locale as SupportedLocale] || defaultText;
  const path = `https://campaign-hub-production-f.squarecdn.com/static/hardware/ar/${device.split(' ').join('-').toLowerCase()}-${
    locale === 'en' ? 'en-us' : locale.toLowerCase()
  }`;

  const svgUniqueId = `clipPath-${Date.now()}-${Math.floor(Math.random() * 10000)}`;
</script>

<svelte:head>
  <script
    async
    type="module"
    src="https://campaign-hub-production-f.squarecdn.com/static/hardware/ar/model-viewer@2.1.1.min.js"
  ></script>
</svelte:head>

<div
  class="HardwareModelViewer HardwareModelViewer-{entryId}-box-properties {customCssClass}"
  data-entry-id={entryId}
  style:--hardware-model-viewer-progress-bar-color={isPWDSEnabled ? 'var(--color-black)' : 'var(--color-blue)'}
>
  <model-viewer
    alt="Square {device}"
    src="{path}.glb"
    ios-src="{path}.usdz"
    ar
    auto-rotate
    camera-controls
    disable-zoom
    interaction-prompt="none"
    rotation-per-second="22deg"
    shadow-intensity="1"
    touch-action="pan-y"
  >
    <button slot="ar-button" class="HardwareModelViewer__button">
      <svg
        class="HardwareModelViewer__svg"
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="20"
        viewBox="0 0 18 20"
        fill="none"
      >
        <g clip-path="url(#svgUniqueId)">
          <path
            d="M17.96 5.31c0-.05-.02-.1-.03-.12-.02-.06-.03-.12-.06-.17-.02-.03-.02-.04-.03-.06a.79.79 0 0 0-.13-.17.31.31 0 0 0-.03-.03c-.03-.03-.05-.07-.09-.1-.03-.03-.07-.05-.11-.07-.03-.02-.06-.05-.09-.07-.03-.01-.05-.03-.08-.04-.03-.01-.06-.02-.09-.03L9.45.61a.79.79 0 0 0-.91 0L.55 4.68c-.03.01-.05.02-.07.03a.62.62 0 0 0-.08.04c-.03.02-.06.05-.09.07-.04.03-.08.06-.11.1-.01.01-.02.02-.03.03a.79.79 0 0 0-.13.17c-.01.02-.01.03-.03.06a.83.83 0 0 0-.06.17.79.79 0 0 0-.03.12c0 .09-.01.18-.01.26v8.96c0 .38.21.73.55.9l8 3.97c.02 0 .03 0 .05.01.13.06.26.09.41.09.14 0 .28-.03.41-.09.02 0 .03 0 .05-.01l8-3.97a1 1 0 0 0 .55-.9V5.57c0-.09 0-.18-.01-.26zm-8.96-2.69 5.77 2.94-4.62 2.29-1.15.57-1.15-.57L3.23 5.56l5.77-2.94zM2 7.18l2.37 1.18 3.63 1.8v6.72L2 13.9V7.18zm14 6.72-6 2.98v-6.72l3.63-1.8L16 7.18v6.72z"
            fill="white"
          />
        </g>
        <defs>
          <clipPath id={svgUniqueId}>
            <rect width="18" height="19" fill="white" transform="translate(0 .5)" />
          </clipPath>
        </defs>
      </svg>
      {text}
    </button>
  </model-viewer>
  <DynamicChildrenComponent children={[content]} />
</div>

<style lang="scss">
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/core/config/_media_queries' as media;
  @use '@squareup/public-web-design-system/dist/styles/xms-fe/mixins/ui.scss' as ui;

  .HardwareModelViewer {
    position: relative;

    model-viewer {
      --progress-bar-height: 4px;
      --progress-bar-color: var(--hardware-model-viewer-progress-bar-color);

      position: relative;

      display: block;
      height: 100%;
      width: 100%;
    }

    &__button {
      @include ui.button;

      position: absolute;
      top: var(--space-10);
      left: var(--space-10);

      display: flex;
      align-items: center;

      @include media.at('medium') {
        left: 50%;

        transform: translateX(-50%);
      }
    }

    &__svg {
      margin-right: 8px;
    }

    :global {
      & > .BoxComponent {
        position: absolute;
        left: 0;
        bottom: 0;
        z-index: 1;
      }
    }
  }

  :global {
    .ModalPartial {
      .HardwareModelViewer {
        height: 100vh;
        max-height: -webkit-fill-available;
        max-height: -moz-available;
        max-height: -webkit-available;
        max-height: fill-available;
      }
    }
  }
</style>
